// DatosComplementariosModal.js — Pide los datos que faltaron en el registro.
//
// Hay campos que no se piden al registrarse (para no alargar el formulario) o
// que son opcionales, pero que luego hacen falta: el teléfono, la ciudad o el
// nombre que debe salir impreso en el certificado. Este modal aparece al entrar
// mientras alguno de esos datos siga vacío, y deja de aparecer solo cuando la
// persona los guarda —no hace falta ninguna cookie: la condición es el dato en
// sí—. Quien lo cierra con la ✕ lo vuelve a ver la próxima vez.
//
// Se configura en Admin → Proyectos → Login y registro → «Datos
// complementarios» (config_json.datos_complementarios) y está apagado salvo que
// el proyecto lo active, así que ningún otro proyecto cambia.
//
// Cada campo toma su forma de los campos del registro de la web del proyecto
// (sección «Solicitar acceso» → registro_campos): la cédula solo acepta números,
// institución/país/especialidad salen como listas con sus opciones (más «Otra»,
// para escribirla) y «acepto_terminos» es una casilla. Lo que no esté en el
// registro sigue siendo una caja de texto, como antes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { BACKEND_BASE, profileService } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { resolverCamposRegistro } from './landing/SolicitudAccesoModal';
import { PAISES_CODIGOS } from '../utils/countryCodes';
import TerminosPopup, { useTerminos } from './TerminosPopup';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3900;
  background: ${p => p.$velo || 'rgba(15, 23, 42, 0.55)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  overflow-y: auto;
`;

const Card = styled.div`
  position: relative;
  width: min(${p => p.$ancho || '820px'}, 100%);
  border-radius: ${p => p.$radio || '10px'};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  padding: ${p => p.$padding || '2.6rem 2.4rem'};
  overflow: hidden;
  background: ${p => p.$bg || '#ffffff'};
  ${p => p.$img && `
    background-image: linear-gradient(${p.$overlay || 'rgba(255,255,255,0.86)'}, ${p.$overlay || 'rgba(255,255,255,0.86)'}), url("${p.$img}");
    background-size: cover;
    background-position: ${p.$pos || 'center'};
  `}
  @media (max-width: 640px) { padding: 1.6rem 1.2rem; }
`;

const Cerrar = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #ffffff;
  color: ${p => p.$color || '#e8481c'};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  &:hover { opacity: 0.85; }
`;

const Titulo = styled.h2`
  margin: 0 0 0.5rem;
  text-align: center;
  color: ${p => p.$color || '#e8481c'};
  font-size: ${p => p.$size || '2rem'};
  font-weight: 800;
  line-height: 1.2;
  @media (max-width: 640px) { font-size: 1.35rem; }
`;

const Subtitulo = styled.p`
  margin: 0 0 1.8rem;
  text-align: center;
  color: ${p => p.$color || '#334155'};
  font-size: ${p => p.$size || '1.05rem'};
`;

const Campo = styled.div`
  max-width: ${p => (p.$enRejilla ? 'none' : (p.$ancho || '360px'))};
  margin: ${p => (p.$enRejilla ? '0' : '0 auto 1.1rem')};
  ${p => p.$completo && 'grid-column: 1 / -1;'}
`;

/* Con `columnas: 2` los campos van de a dos (uno solo en el celular). */
const Rejilla = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.2rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: ${p => p.$padding || '0.7rem 0.9rem'};
  border: 1px solid ${p => p.$borde || '#d8dde6'};
  border-radius: ${p => p.$radio || '8px'};
  font-size: 0.95rem;
  font-family: inherit;
  background: #ffffff;
  &:focus { outline: none; border-color: ${p => p.$foco || '#e8481c'}; }
`;

const Casilla = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.95rem;
  color: ${p => p.$color || '#1f2b45'};
  cursor: pointer;
  input { width: 18px; height: 18px; margin: 2px 0 0; flex: none; accent-color: ${p => p.$foco || '#e8481c'}; }
  a { color: ${p => p.$foco || '#e8481c'}; font-weight: 700; }
  .req { color: #dc2626; }
  .ver-terminos {
    border: none; background: none; padding: 0; font: inherit; text-align: left; cursor: pointer;
    color: ${p => p.$foco || '#e8481c'}; font-weight: 700; text-decoration: underline; text-underline-offset: 2px;
  }
`;

const AvisoError = styled.p`
  margin: 1rem 0 0;
  text-align: center;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const Etiqueta = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: ${p => p.$color || '#1f2b45'};
  .req { color: #dc2626; }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${p => p.$padding || '0.7rem 0.9rem'};
  border: 1px solid ${p => p.$borde || '#d8dde6'};
  border-radius: ${p => p.$radio || '8px'};
  font-size: 0.95rem;
  font-family: inherit;
  background: #ffffff;
  &:focus { outline: none; border-color: ${p => p.$foco || '#e8481c'}; }
`;

const Acciones = styled.div`
  text-align: center;
  margin-top: 1.6rem;
`;

const Boton = styled.button`
  background: ${p => p.$bg || '#f4441d'};
  color: ${p => p.$color || '#ffffff'};
  border: none;
  border-radius: ${p => p.$radio || '6px'};
  padding: ${p => p.$padding || '0.85rem 2.6rem'};
  font-family: inherit;
  font-size: ${p => p.$size || '1.05rem'};
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

// Campos que viven en columnas de `users`; el resto va a los personalizados.
const COLUMNAS = ['nombre', 'telefono', 'codigo_pais', 'especialidad', 'pais', 'dni'];

// Etiqueta por defecto de los campos conocidos, por si el registro no la trae.
const ETIQUETAS = {
  telefono: 'Teléfono',
  ciudad: 'Ciudad',
  nombre_certificado: 'Nombre a mostrar certificado',
  nombre: 'Nombre completo',
  dni: 'Cédula',
  institucion: 'Institución',
  pais: 'País',
  especialidad: 'Especialidad',
  acepto_terminos: 'Acepto términos y condiciones',
};

// Nombre del campo en el registro de la web cuando no coincide con la cuenta.
const CLAVE_REGISTRO = { dni: 'documento' };
// Forma por defecto si el registro no dice otra cosa.
const TIPO_DEFECTO = { dni: 'numero', telefono: 'tel', acepto_terminos: 'checkbox' };
const OTRA = 'Otra';
const PAISES = PAISES_CODIGOS.map(p => p.nombre).sort((a, b) => a.localeCompare(b, 'es'));
const ACEPTADO = ['sí', 'si', 'true', '1', 'yes'];

const textoDe = (v, fb = '') => {
  if (v == null) return fb;
  if (typeof v === 'object') return v.es || Object.values(v)[0] || fb;
  return String(v) || fb;
};

const DatosComplementariosModal = () => {
  const { proyecto } = useProject();
  const { user } = useAuth();
  const cfg = useMemo(() => proyecto?.config_json?.datos_complementarios || {}, [proyecto]);

  const [profile, setProfile] = useState(null);
  const [registroConfig, setRegistroConfig] = useState(null);
  const [valores, setValores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [cerrado, setCerrado] = useState(false);
  const [error, setError] = useState('');
  // Casilla cuyos términos se están leyendo (el pop up la marca al aceptar).
  const [verTerminos, setVerTerminos] = useState(null);
  const terminos = useTerminos();
  const cerrarTerminos = useCallback(() => setVerTerminos(null), []);

  // El administrador de la plataforma no tiene por qué llenar datos de alumno.
  const activo = cfg.activo === true && !!user && user.role !== 'admin';

  useEffect(() => {
    if (!activo) return;
    let vivo = true;
    profileService.getProfile()
      .then(res => {
        if (!vivo) return;
        setProfile(res.data?.user || null);
        setRegistroConfig(res.data?.registro_config || null);
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [activo]);

  // Campos y listas del registro de la web del proyecto (formulario «Crear cuenta»).
  const registroWeb = useMemo(() => {
    const seccion = (proyecto?.config_json?.secciones_globales || []).find(x => x?.tipo === 'solicitud_acceso');
    const conf = seccion?.config || {};
    return {
      campos: seccion ? resolverCamposRegistro(conf, v => textoDe(v)) : [],
      especialidades: Array.isArray(conf.especialidades) ? conf.especialidades : [],
    };
  }, [proyecto]);

  // Tipo, etiqueta y opciones de cada campo.
  const meta = useCallback((key) => {
    const reg = registroWeb.campos.find(c => c.key === (CLAVE_REGISTRO[key] || key));
    const campoCuenta = (registroConfig?.campos || []).find(c => c.key === key);
    let opciones = Array.isArray(reg?.opciones) ? reg.opciones.filter(Boolean) : [];
    if (!opciones.length && key === 'pais') opciones = PAISES;
    if (!opciones.length && key === 'especialidad') opciones = registroWeb.especialidades;
    let tipo = TIPO_DEFECTO[key] || reg?.tipo || 'text';
    if (['select', 'pais', 'especialidad'].includes(tipo) || opciones.length) tipo = opciones.length ? 'select' : 'text';
    return {
      tipo,
      opciones: opciones.filter(o => o !== OTRA),
      etiqueta: textoDe(cfg.etiquetas?.[key]) || textoDe(reg?.label) || textoDe(campoCuenta?.etiqueta) || ETIQUETAS[key] || key,
      enlace: cfg.url_terminos || reg?.enlace_url || '',
    };
  }, [registroWeb, registroConfig, cfg.etiquetas, cfg.url_terminos]);

  // Valor actual de un campo, mire donde mire (columna o campo personalizado).
  const valorDe = useCallback((key) => {
    if (!profile) return '';
    const v = COLUMNAS.includes(key)
      ? profile[key]
      : (profile.campos_personalizados || {})[key];
    return typeof v === 'string' ? v.trim() : (v == null ? '' : String(v));
  }, [profile]);

  const claves = useMemo(() => (Array.isArray(cfg.campos) ? cfg.campos : []), [cfg.campos]);
  // Solo se pregunta lo que falta: quien ya tiene el dato no ve el modal.
  // La casilla de términos cuenta como llena solo si quedó aceptada.
  const faltantes = useMemo(
    () => (profile ? claves.filter(k => (
      k === 'acepto_terminos' ? !ACEPTADO.includes(valorDe(k).toLowerCase()) : valorDe(k) === ''
    )) : []),
    [profile, claves, valorDe]
  );

  const obligatorios = Array.isArray(cfg.obligatorios) && cfg.obligatorios.length ? cfg.obligatorios : claves;
  // Valor final de un campo del modal (la lista con «Otra» usa lo que se escribió).
  const valorFinal = (k) => {
    const v = valores[k];
    if (v === true) return 'Sí';
    if (v === OTRA) return (valores[`${k}_otra`] || '').trim();
    return typeof v === 'string' ? v.trim() : '';
  };
  const falta = faltantes.some(k => obligatorios.includes(k) && !valorFinal(k));

  const guardar = async () => {
    setError('');
    const dni = faltantes.includes('dni') ? valorFinal('dni') : '';
    if (dni && !/^\d{4,20}$/.test(dni)) { setError('La cédula debe contener solo números.'); return; }
    const tel = faltantes.includes('telefono') ? valorFinal('telefono') : '';
    if (tel && tel.replace(/\D/g, '').length < 7) { setError('Por favor ingresa un teléfono válido.'); return; }

    setGuardando(true);
    try {
      const payload = {};
      const custom = {};
      faltantes.forEach(k => {
        const v = valorFinal(k);
        if (!v) return;
        if (COLUMNAS.includes(k)) payload[k] = v;
        else custom[k] = v;
        if (k === 'acepto_terminos') custom.acepto_terminos_fecha = new Date().toISOString();
      });
      if (Object.keys(custom).length) payload.campos_personalizados = custom;
      const res = await profileService.updateProfile(payload);
      if (res.data?.user) setProfile(prev => ({ ...prev, ...res.data.user }));
      toast.success(textoDe(cfg.texto_guardado, 'Gracias, tus datos quedaron actualizados'));
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo guardar. Inténtalo de nuevo.');
    }
    setGuardando(false);
  };

  if (!activo || cerrado || !profile || faltantes.length === 0) return null;

  const fondo = cfg.imagen_fondo
    ? (cfg.imagen_fondo.startsWith('http') ? cfg.imagen_fondo : `${BACKEND_BASE}/${cfg.imagen_fondo.replace(/^\//, '')}`)
    : '';
  const enRejilla = Number(cfg.columnas) === 2;
  const estiloCampo = { $padding: cfg.padding_campo, $borde: cfg.color_borde_campo, $radio: cfg.radio_campo, $foco: cfg.color_borde_foco };
  const cambiar = (k, v) => setValores(prev => ({ ...prev, [k]: v }));

  const control = (k) => {
    const m = meta(k);
    const id = `dc_${k}`;
    const req = obligatorios.includes(k) && <span className="req"> *</span>;
    if (m.tipo === 'checkbox') {
      return (
        <Campo key={k} $enRejilla={enRejilla} $completo>
          <Casilla $color={cfg.color_etiqueta} $foco={cfg.color_borde_foco}>
            <input type="checkbox" checked={valores[k] === true} onChange={e => cambiar(k, e.target.checked)} />
            <span>
              {terminos.hay ? (
                <button type="button" className="ver-terminos" onClick={(e) => { e.preventDefault(); setVerTerminos(k); }}>
                  {m.etiqueta}
                </button>
              ) : m.enlace
                ? <a href={m.enlace} target="_blank" rel="noopener noreferrer">{m.etiqueta}</a>
                : m.etiqueta}
              {req}
            </span>
          </Casilla>
        </Campo>
      );
    }
    return (
      <Campo key={k} $ancho={cfg.ancho_campo} $enRejilla={enRejilla}>
        <Etiqueta htmlFor={id} $color={cfg.color_etiqueta}>{m.etiqueta}{req}</Etiqueta>
        {m.tipo === 'select' ? (
          <>
            <Select id={id} value={valores[k] || ''} onChange={e => cambiar(k, e.target.value)} {...estiloCampo}>
              <option value="">{k === 'pais' ? 'Elige un país' : 'Selecciona una opción'}</option>
              {m.opciones.map(o => <option key={o} value={o}>{o}</option>)}
              {k !== 'pais' && <option value={OTRA}>{OTRA}</option>}
            </Select>
            {valores[k] === OTRA && (
              <Input
                style={{ marginTop: '0.5rem' }}
                value={valores[`${k}_otra`] || ''}
                onChange={e => cambiar(`${k}_otra`, e.target.value)}
                placeholder={`Escribe tu ${m.etiqueta.toLowerCase()}`}
                aria-label={`Otra ${m.etiqueta.toLowerCase()}`}
                {...estiloCampo}
              />
            )}
          </>
        ) : (
          <Input
            id={id}
            value={valores[k] || ''}
            onChange={e => cambiar(k, m.tipo === 'numero' ? e.target.value.replace(/\D/g, '') : e.target.value)}
            placeholder={m.tipo === 'numero' ? 'Solo números' : m.etiqueta}
            inputMode={m.tipo === 'numero' ? 'numeric' : (m.tipo === 'tel' ? 'tel' : undefined)}
            type={m.tipo === 'tel' ? 'tel' : 'text'}
            {...estiloCampo}
          />
        )}
      </Campo>
    );
  };

  return (
    <Overlay $velo={cfg.color_velo}>
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby="dc_titulo"
        $ancho={cfg.ancho}
        $radio={cfg.radio}
        $padding={cfg.padding}
        $bg={cfg.color_fondo}
        $img={fondo}
        $overlay={cfg.overlay}
        $pos={cfg.posicion_fondo}
      >
        {cfg.permitir_cerrar !== false && (
          <Cerrar type="button" aria-label="Cerrar" onClick={() => setCerrado(true)} $color={cfg.color_cerrar}>
            ✕
          </Cerrar>
        )}
        <Titulo id="dc_titulo" $color={cfg.color_titulo} $size={cfg.tamano_titulo}>
          {textoDe(cfg.titulo, 'Actualiza tu información')}
        </Titulo>
        {textoDe(cfg.subtitulo) !== '' && (
          <Subtitulo $color={cfg.color_subtitulo} $size={cfg.tamano_subtitulo}>
            {textoDe(cfg.subtitulo)}
          </Subtitulo>
        )}

        {enRejilla ? <Rejilla>{faltantes.map(control)}</Rejilla> : faltantes.map(control)}

        {error && <AvisoError role="alert">{error}</AvisoError>}
        <TerminosPopup abierto={!!verTerminos} onCerrar={cerrarTerminos} onAceptar={() => cambiar(verTerminos, true)} />

        <Acciones>
          <Boton
            type="button"
            onClick={guardar}
            disabled={guardando || falta}
            $bg={cfg.color_boton}
            $color={cfg.color_texto_boton}
            $radio={cfg.radio_boton}
            $padding={cfg.padding_boton}
            $size={cfg.tamano_boton}
          >
            {guardando ? textoDe(cfg.texto_guardando, 'Guardando…') : textoDe(cfg.texto_boton, 'Guardar')}
          </Boton>
        </Acciones>
      </Card>
    </Overlay>
  );
};

export default DatosComplementariosModal;
